import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { userInformation } from '@redux/actions/User';
import useUser from 'hooks/ui/useUser';
import { ApiResponse } from 'further-types/api';
import { Api } from 'further-types/further-user';
import { AccountType } from 'further-types/investor';
import { Country, EuCountries } from 'further-ui/labels';
import showRequestError from 'helpers/layout/showRequestError';
import { QUERY_STALE_TIME_MS } from 'further-ui/config';
import { useWhiteLabellingContext } from 'contexts/WhiteLabellingContext';
import getUserType from 'utils/getUserType';

type Options = {
  alwaysEnabled?: boolean;
  alwaysDisabled?: boolean;
};

export const fetchCurrentUserInfo = async (input?: {
  isSubdomain?: boolean;
}) => {
  const response = await axios.get<ApiResponse<Api.GetCurrentUserResponse>>(
    `/further-user/current-user-info`,
    { params: { isSubdomain: input?.isSubdomain } },
  );
  return response.data.data;
};

const investorJustCompletedOnboarding = (
  currentState: { isOnbordingComplete?: boolean },
  nextState: { isOnbordingComplete?: boolean },
) => {
  return (
    currentState.isOnbordingComplete === false &&
    nextState.isOnbordingComplete === true
  );
};

const investorJustBecameNonTemporary = (
  currentState: { isAccountTemporary?: boolean },
  nextState: { isAccountTemporary?: boolean },
) => {
  return (
    currentState.isAccountTemporary === true &&
    nextState.isAccountTemporary === false
  );
};

const investorJustChangedFullName = (
  currentState: { fullName?: string },
  nextState: { fullName?: string },
) => {
  return currentState.fullName !== nextState.fullName;
};

const investorPermissionsAreDifferent = (
  currentState: { investorAccessPermissions?: Array<string> },
  nextState: { investorAccessPermissions?: Array<string> },
) => {
  const current = currentState.investorAccessPermissions ?? [];
  const next = nextState.investorAccessPermissions ?? [];

  return (
    current.length !== next.length ||
    !current.every((permission) => next.includes(permission))
  );
};

export function useCurrentUserInfo(options?: Options) {
  const dispatch = useDispatch();
  const user = useUser();
  const queryClient = useQueryClient();
  const { isSubdomain } = useWhiteLabellingContext();
  const { userIsInvestor } = getUserType(user);

  return useQuery({
    queryKey: ['current-user-info'],
    queryFn: async () => {
      try {
        let isUKInvestor = false;
        let isEUInvestor = false;
        let isUSInvestor = false;
        const account = await fetchCurrentUserInfo({ isSubdomain });
        const country =
          account?.registrationCountry ?? account?.address?.[0]?.country;

        if (userIsInvestor && country) {
          isUKInvestor = country === Country.UnitedKingdom;
          isEUInvestor = EuCountries.includes(country as Country);
          isUSInvestor = country === Country.UnitedStates;
        }

        const userInfo = {
          ...account,
          country,
          isIndividual: account?.accountType === AccountType.Individual,
          isLegalEntity: account?.accountType === AccountType.LegalEntity,
          isUKInvestor,
          isEUInvestor,
          isUSInvestor,
        };

        // Check if the user in the API response has changed from the user stored
        // in the redux state. This could happen when a user switches accounts or
        // their account is updated.
        if (
          !user.isAuthenticated ||
          user._id !== account._id ||
          (userIsInvestor &&
            (investorJustCompletedOnboarding(user, userInfo) ||
              investorJustBecameNonTemporary(user, userInfo) ||
              investorJustChangedFullName(user, userInfo) ||
              investorPermissionsAreDifferent(user, userInfo)))
        ) {
          dispatch<any>(
            userInformation({
              ...userInfo,
              isAuthenticated: true,
            }),
          );

          // clear the cache so the latest user data is always used
          queryClient.clear();
        }

        return userInfo;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          return;
        }
        const error = err as Error;
        showRequestError(error);
      }
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    staleTime: QUERY_STALE_TIME_MS,
    refetchOnReconnect: true,
    refetchOnMount: false,
    enabled: options?.alwaysDisabled
      ? false
      : options?.alwaysEnabled ||
        (!!user._id && !!user.isAuthenticated && !user.authGateDecision),
  });
}
